import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ExternalLink, Sparkles, Lock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECT_LABEL = {
  business: "Business",
  tech: "Tech",
  ideas: "Ideas & Culture",
  money: "Money",
  travel: "Travel",
  lifestyle: "Lifestyle & Home",
  science: "Science",
  auto: "Auto",
};
const SUBJECT_ORDER = ["business", "tech", "ideas", "money", "travel", "lifestyle", "science", "auto"];

export default function Newsstand() {
  const [items, setItems] = useState([]);
  const [publishers, setPublishers] = useState({});
  const [subject, setSubject] = useState("");
  const [publisher, setPublisher] = useState("");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isPro = (user?.tier || "") === "pro" || (user?.tier || "") === "business";

  useEffect(() => {
    axios.get(`${API}/newsstand/publishers`).then((r) => setPublishers(r.data?.by_subject || {})).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 40 };
    if (subject) params.subject = subject;
    if (publisher) params.publisher = publisher;
    axios.get(`${API}/newsstand/articles`, { params })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [subject, publisher]);

  return (
    <>
      <Header />
      <main className="max-w-[1280px] mx-auto px-5 py-8">
        {/* Page header */}
        <section className="mb-6 pb-5 border-b border-[var(--line)]" data-testid="newsstand-header">
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[#1e3a8a] text-white"><BookOpen size={20} /></span>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-black text-[var(--blue)]">Newseze · Newsstand</div>
              <h1 className="text-[28px] md:text-[34px] font-black tracking-[-0.025em] text-[var(--ink)] leading-none">The Magazines</h1>
            </div>
          </div>
          <p className="text-[14px] text-[var(--muted)] max-w-[680px] leading-snug">
            Long-form features from {Object.values(publishers).flat().length}+ mainstream magazines and newspaper opinion sections. You pick what to read — we don't pre-rank by publisher. Newseze brief is free on every article. Open the original at the publisher in one click.
          </p>
        </section>

        {/* Subject + publisher facets */}
        <section className="mb-6" data-testid="newsstand-facets">
          <div className="flex flex-wrap gap-1.5 mb-3">
            <button
              onClick={() => { setSubject(""); setPublisher(""); }}
              className={`text-[11.5px] uppercase tracking-[0.14em] font-black px-3 py-1.5 rounded-md transition ${!subject ? "bg-[var(--ink)] text-white" : "bg-white border border-[var(--line)] text-[var(--ink)] hover:border-[var(--blue)]"}`}
              data-testid="facet-all"
            >
              All subjects
            </button>
            {SUBJECT_ORDER.filter((s) => publishers[s]?.length).map((s) => (
              <button
                key={s}
                onClick={() => { setSubject(s); setPublisher(""); }}
                className={`text-[11.5px] uppercase tracking-[0.14em] font-black px-3 py-1.5 rounded-md transition ${subject === s ? "bg-[var(--ink)] text-white" : "bg-white border border-[var(--line)] text-[var(--ink)] hover:border-[var(--blue)]"}`}
                data-testid={`facet-subject-${s}`}
              >
                {SUBJECT_LABEL[s] || s}
              </button>
            ))}
          </div>

          {subject && publishers[subject]?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pb-3" data-testid="newsstand-publishers">
              <button onClick={() => setPublisher("")} className={`text-[11px] uppercase tracking-[0.14em] font-bold px-2.5 py-1 rounded ${!publisher ? "bg-[var(--gold)] text-white" : "bg-[#fff9ed] border border-[var(--line)] text-[var(--ink)] hover:border-[var(--gold)]"}`}>
                All in {SUBJECT_LABEL[subject] || subject}
              </button>
              {publishers[subject].map((p) => (
                <button
                  key={p.handle}
                  onClick={() => setPublisher(p.handle)}
                  className={`text-[11px] uppercase tracking-[0.14em] font-bold px-2.5 py-1 rounded ${publisher === p.handle ? "bg-[var(--gold)] text-white" : "bg-[#fff9ed] border border-[var(--line)] text-[var(--ink)] hover:border-[var(--gold)]"}`}
                  data-testid={`facet-pub-${p.handle}`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Items grid */}
        {loading ? (
          <div className="text-[var(--muted)] text-[14px] italic">Loading magazines…</div>
        ) : items.length === 0 ? (
          <div className="text-[var(--muted)] text-[14px] italic">No articles yet for this filter. Check back soon.</div>
        ) : (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="newsstand-grid">
            {items.map((a) => <MagazineCard key={a.id} article={a} isPro={isPro} />)}
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

export function MagazineCard({ article, isPro }) {
  return (
    <article className="bg-white rounded-lg border border-[var(--line)] overflow-hidden flex flex-col hover:border-[var(--blue)] transition" data-testid={`magazine-card-${article.id}`}>
      {article.image && (
        <a href={article.link} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer" className="block aspect-[16/10] bg-cover bg-center" style={{ backgroundImage: `url(${article.image})` }}>
          <span className="sr-only">{article.title}</span>
        </a>
      )}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          <span className="text-[9.5px] uppercase tracking-[0.16em] font-black text-[var(--blue)]">{article.publisher}</span>
          <span className="text-[9px] uppercase tracking-[0.14em] font-black bg-emerald-100 text-emerald-700 px-1 rounded">free brief</span>
        </div>
        <h3 className="text-[15.5px] font-black text-[var(--ink)] leading-snug mb-1.5 line-clamp-3">{article.title}</h3>
        <p className="text-[12.5px] text-[var(--muted)] leading-snug mb-3 line-clamp-3">{article.summary}</p>
        <div className="mt-auto flex flex-wrap gap-1.5">
          <a href={article.link} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer" className="btn text-[12px] flex-1 justify-center" data-testid={`magazine-read-${article.id}`}>
            <ExternalLink size={12} /> Read at {article.publisher.split(" ")[0]}
          </a>
          {isPro ? (
            <button className="btn gold text-[12px]" data-testid={`magazine-pro-${article.id}`} title="Newseze Pro Analysis — included with your tier">
              <Sparkles size={12} /> Pro Analysis
            </button>
          ) : (
            <button className="btn text-[12px] bg-[#fff9ed] border-[var(--gold)] text-[var(--ink)] hover:bg-[#fff3d6]" data-testid={`magazine-buy-${article.id}`} title="$1 single purchase OR included free with Pro ($4.99/mo)">
              <Lock size={11} /> +$1 Analysis
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
